import { Patient } from "../../../services/patient.service";

export type HistorySortOrder = "alphabetical" | "last-consultation" | "consultation-count";

export const HISTORY_SORT_OPTIONS: Array<{ value: HistorySortOrder; label: string }> = [
  { value: "alphabetical", label: "A-Z" },
  { value: "last-consultation", label: "Última consulta" },
  { value: "consultation-count", label: "Nº de consultas" },
];

export const HISTORY_ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

const collator = new Intl.Collator("pt-BR", { sensitivity: "base" });

export interface HistoryConsultation {
  id: number;
  type: string;
  date: string;
  focus: string;
  patientId: number;
  patientName: string;
}

const normalize = (value: string | null | undefined) =>
  (value ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

const compareDatesDesc = (left: string, right: string) => new Date(right).getTime() - new Date(left).getTime();

const comparePatientNameAsc = (left: string, right: string) => collator.compare(left.trim(), right.trim());

export const isOralConsultation = (item: HistoryConsultation) => item.type === "ORAL";

export function flattenConsultations(patients: Patient[]): HistoryConsultation[] {
  return patients
    .flatMap((patient) =>
      (patient.consultations ?? []).map((consultation) => ({
        id: consultation.id,
        type: consultation.type,
        date: consultation.date,
        focus: consultation.name,
        patientId: patient.id,
        patientName: patient.name,
      }))
    )
    .sort((a, b) => compareDatesDesc(a.date, b.date));
}

export function filterConsultations(items: HistoryConsultation[], query: string): HistoryConsultation[] {
  const q = normalize(query.trim());
  if (!q) return items;
  return items.filter((item) => [item.patientName, item.focus].some((field) => normalize(field).includes(q)));
}

export interface PatientGroup {
  patientId: number;
  patientName: string;
  consultations: HistoryConsultation[];
  lastDate: string;
}

export interface AlphabetIndexEntry {
  letter: string;
  available: boolean;
  patientId: number | null;
}

export function getPatientInitialLetter(patientName: string): string | null {
  const first = normalize(patientName).replace(/[^a-z]/g, "").charAt(0).toUpperCase();
  return HISTORY_ALPHABET.includes(first) ? first : null;
}

function sortPatientGroups(groups: PatientGroup[], sortOrder: HistorySortOrder): PatientGroup[] {
  return [...groups].sort((left, right) => {
    if (sortOrder === "alphabetical") {
      return comparePatientNameAsc(left.patientName, right.patientName) || compareDatesDesc(left.lastDate, right.lastDate);
    }

    if (sortOrder === "consultation-count") {
      return right.consultations.length - left.consultations.length || compareDatesDesc(left.lastDate, right.lastDate) || comparePatientNameAsc(left.patientName, right.patientName);
    }

    return compareDatesDesc(left.lastDate, right.lastDate) || comparePatientNameAsc(left.patientName, right.patientName);
  });
}

export function groupByPatient(items: HistoryConsultation[], sortOrder: HistorySortOrder = "last-consultation"): PatientGroup[] {
  const groups = new Map<number, PatientGroup>();
  for (const item of items) {
    const existing = groups.get(item.patientId);
    if (existing) {
      existing.consultations.push(item);
      if (new Date(item.date).getTime() > new Date(existing.lastDate).getTime()) existing.lastDate = item.date;
    } else {
      groups.set(item.patientId, {
        patientId: item.patientId,
        patientName: item.patientName,
        consultations: [item],
        lastDate: item.date,
      });
    }
  }
  return sortPatientGroups(Array.from(groups.values()), sortOrder);
}

export function buildAlphabetIndex(groups: PatientGroup[]): AlphabetIndexEntry[] {
  const firstPatientByLetter = new Map<string, number>();

  groups.forEach((group) => {
    const letter = getPatientInitialLetter(group.patientName);
    if (letter && !firstPatientByLetter.has(letter)) {
      firstPatientByLetter.set(letter, group.patientId);
    }
  });

  return HISTORY_ALPHABET.map((letter) => ({
    letter,
    available: firstPatientByLetter.has(letter),
    patientId: firstPatientByLetter.get(letter) ?? null,
  }));
}
